import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import formatDate from 'date-fns/format'

import { apiError } from '@ads/front-core/store/sagas/apiErrorSaga'
import {
  Currency,
  CurrencyDetails,
  ExchangeApiResponse,
  DEFAULT_CURRENCY,
} from '@ads/front-core/interfaces'
import { CURRENCIES } from '@ads/front-core/utils'
import { getCurrentCurrency } from '@ads/front-core/store/selectors/getCurrentCurrency'
import { getCurrencyData } from '@ads/front-core/store/selectors/getCurrencyData'
import { getCurrencyLoading } from '@ads/front-core/store/selectors/getCurrencyLoading'
import { setCurrencyData } from '@ads/front-core/store/reducers/currency/setCurrencyDataReducer'
import { setCurrencyLoading } from '@ads/front-core/store/reducers/currency/setCurrencyLoadingReducer'

export type UseCurrencyReturn = {
  loading: boolean
  currencyKey: Currency
  currency: CurrencyDetails
  currencyDate: Date
  isDefaultCurrency: boolean
  calculateCurrency: (val: number, forceCurrency?: Currency) => number
  calculateCurrencyWithDate: (val: number, date?: string) => Promise<number>
}

export const useCurrency = (): UseCurrencyReturn => {
  const dispatch = useDispatch()
  const storedData = useSelector(getCurrencyData)
  const currencyKey = useSelector(getCurrentCurrency) || DEFAULT_CURRENCY
  const today = formatDate(new Date(), 'yyyy-MM-dd')

  const dates = Object.keys(storedData || {}).sort()
  const latestDate = dates?.[dates?.length - 1]

  const loading = useSelector(getCurrencyLoading)

  const isDefaultCurrency = !currencyKey || currencyKey === DEFAULT_CURRENCY

  const fetchData = async (
    date?: string,
  ): Promise<Record<Currency, number> | null> => {
    const fetchDate = date || today

    if (storedData?.[fetchDate]) {
      return storedData?.[fetchDate]
    }

    dispatch(setCurrencyLoading(true))

    try {
      const res = await fetch(`/api-currency/${fetchDate}/`)
      const json = await res.json()
      const jsonData = json as ExchangeApiResponse
      const data = jsonData.reduce(
        (acc, item) => ({ ...acc, [item.currency]: item.value }),
        {} as Record<Currency, number>,
      )
      dispatch(
        setCurrencyData({
          date: jsonData?.[0]?.effective_date || today,
          data,
        }),
      )
      dispatch(setCurrencyLoading(false))
      return data
    } catch (error) {
      dispatch(apiError({ error }))
      dispatch(setCurrencyLoading(false))
    }
    return null
  }

  const calculateCurrencyWithDate = async (
    val: number,
    date?: string,
  ): Promise<number> => {
    if (!currencyKey || isDefaultCurrency) {
      return val
    }
    const calculateDate = date || today
    let currencyRate = storedData?.[calculateDate]?.[currencyKey]
    if (!currencyRate) {
      const data = await fetchData(calculateDate)
      if (!data) {
        return 0
      }
      currencyRate = data?.[currencyKey]
    }
    return val / currencyRate
  }

  const calculateCurrency = (val: number, forceCurrency?: Currency): number => {
    const calcKey = forceCurrency || currencyKey

    if (!calcKey || calcKey === DEFAULT_CURRENCY) {
      return val
    }
    const currencyRate = storedData?.[latestDate]?.[calcKey]
    return val / currencyRate
  }

  const currencyDate = new Date(latestDate)

  const currency: CurrencyDetails = {
    ...CURRENCIES.find(c => c.key === currencyKey),
    rate: storedData?.[latestDate]?.[currencyKey],
    date: currencyDate,
  }

  useEffect(() => {
    fetchData(latestDate)
  }, [latestDate])

  return {
    loading,
    currencyKey,
    currency,
    currencyDate,
    isDefaultCurrency,
    calculateCurrency,
    calculateCurrencyWithDate,
  }
}
